/**
 * Cross-cutting product constants that belong to neither the enum catalogue nor a single
 * contract module, and that both `apps/api` and `apps/web` must agree on exactly.
 *
 * **There is deliberately no terms/privacy *version* constant here** (C-2, §16). A legal
 * version is a database fact: `terms_versions` pins the exact document by `content_hash`, and
 * consent references that row by id. A `LEGAL_DOCUMENTS` constant in the frontend plus a
 * `TERMS_VERSION_*` environment variable in the API would be two more sources of truth for
 * something that has to be provable. What lives here instead is which *kinds* of document each
 * consent flow must collect — the shape of the flow, not the version of the paper.
 */
import type { TermsKind } from './enums.js';

/** The active-organization header. This exact spelling, everywhere (§12.1, C-25). */
export const ORGANIZATION_HEADER = 'X-Tvitvi-Organization';

/**
 * The header's name before the rename to Tvitvi (October 2026). The API still ACCEPTS it, after the
 * new one, so a browser holding a cached old bundle keeps working through a deploy. Nothing sends it.
 */
export const LEGACY_ORGANIZATION_HEADER = 'X-Chorus-Organization';

/**
 * Correlates a request across the HTTP log, `audit_log.request_id` and `event_log`. Accepted
 * inbound only when it parses as a UUID, otherwise generated; always echoed (§12.1, C-7).
 */
export const REQUEST_ID_HEADER = 'X-Request-Id';

/**
 * Gjentakelsesvernet på opprettelsene (AI-ANALYSE §8.5): samme nøkkel og samme kropp innen 24 timer
 * gir det lagrede svaret, ikke en rad til. Svaret på en gjentakelse bærer `Idempotent-Replayed: true`.
 */
export const IDEMPOTENCY_HEADER = 'Idempotency-Key';
export const IDEMPOTENT_REPLAY_HEADER = 'Idempotent-Replayed';

/** Base path for every versioned endpoint. Health and the OpenAPI document sit outside it. */
export const API_VERSION_PREFIX = '/v1';

/**
 * The documents a *person* accepts for themselves, on first sign-in
 * (`POST /v1/auth/session`). Both must be current for the session to be unblocked.
 */
export const USER_CONSENT_KINDS = [
  'user_terms',
  'privacy_policy',
] as const satisfies readonly TermsKind[];
export type UserConsentKind = (typeof USER_CONSENT_KINDS)[number];

/**
 * The documents the founding superadmin accepts on the *organization's* behalf when creating it
 * (`POST /v1/organizations`). Both are required; it is not a checkbox the frontend may skip.
 */
export const ORGANIZATION_CONSENT_KINDS = [
  'customer_terms',
  'dpa',
] as const satisfies readonly TermsKind[];
export type OrganizationConsentKind = (typeof ORGANIZATION_CONSENT_KINDS)[number];

/**
 * Version stamp of the default permission template in `ROLES.md` §4 and
 * `policy/default-permissions.ts`. Bumped when the template a *new* organization starts with
 * changes; existing organizations keep their own `role_permissions` rows (decision 0001).
 */
export const PERMISSION_TEMPLATE_VERSION = '2026-08-26';

/** Defaults offered when creating an organization; every one of them is editable afterwards. */
export const DEFAULT_COUNTRY_CODE = 'NO';
export const DEFAULT_TIMEZONE = 'Europe/Oslo';
